import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Scale, Droplet, Wheat, Pill, AlertTriangle, ChevronRight, Sparkles, Info } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// ---- Breed + condition tables (matches user spec) --------------------------

const BREEDS = [
  { id: "shetland", nl: "Shetlander", en: "Shetland pony", correction: -0.12 },
  { id: "welsh", nl: "Welsh Pony", en: "Welsh pony", correction: -0.10 },
  { id: "miniature", nl: "Miniatuurpaard", en: "Miniature horse", correction: -0.15 },
  { id: "arabian", nl: "Arabier", en: "Arabian", correction: -0.08 },
  { id: "thoroughbred", nl: "Volbloed", en: "Thoroughbred", correction: -0.05 },
  { id: "warmblood", nl: "Warmbloed", en: "Warmblood", correction: 0 },
  { id: "haflinger", nl: "Haflinger", en: "Haflinger", correction: 0.05 },
  { id: "fjord", nl: "Fjord", en: "Fjord", correction: 0.06 },
  { id: "draft", nl: "Trekpaard", en: "Draft horse", correction: 0.15 },
  { id: "donkey", nl: "Ezel", en: "Donkey", correction: -0.10 },
  { id: "other", nl: "Anders", en: "Other", correction: 0 },
];

const CONDITIONS = [
  { id: "very_thin", nl: "Zeer mager (1-2)", en: "Very thin (1-2)", correction: -0.10, score: 1.5 },
  { id: "thin", nl: "Mager (3)", en: "Thin (3)", correction: -0.05, score: 3 },
  { id: "ideal", nl: "Ideaal (4-5)", en: "Ideal (4-5)", correction: 0, score: 4.5 },
  { id: "slight_over", nl: "Licht overgewicht (6)", en: "Slight overweight (6)", correction: 0.05, score: 6 },
  { id: "over", nl: "Overgewicht (7)", en: "Overweight (7)", correction: 0.10, score: 7 },
  { id: "severe_over", nl: "Ernstig overgewicht (8-9)", en: "Severe overweight (8-9)", correction: 0.15, score: 8.5 },
];

const AGES = [
  { id: "foal", nl: "Veulen", en: "Foal" },
  { id: "young", nl: "Jong paard", en: "Young horse" },
  { id: "adult", nl: "Volwassen", en: "Adult" },
  { id: "senior", nl: "Senior", en: "Senior" },
];

const WEIGHT_DISCLAIMER_NL = "Een gewichtscalculator geeft een schatting. Voor exacte medicatiedoseringen gebruik je altijd een weegbrug.";
const WEIGHT_DISCLAIMER_EN = "A weight calculator gives an estimate. Always use a livestock scale for exact medication dosing.";

// ---- Pure computation -----------------------------------------------------

const computeWeight = ({ heart, length, height, breed, condition }) => {
  // Use Carroll-Huntington formula if length is given, otherwise estimate length from height.
  const effectiveLength = length && length > 0 ? length : Math.round(height * 1.05);
  const base = (heart * heart * effectiveLength) / 11877;
  const breedCorr = BREEDS.find((b) => b.id === breed)?.correction || 0;
  const condCorr = CONDITIONS.find((c) => c.id === condition)?.correction || 0;
  const adjusted = base * (1 + breedCorr) * (1 + condCorr);
  // 5% range either side
  return {
    base: Math.round(base),
    estimated: Math.round(adjusted),
    low: Math.round(adjusted * 0.95),
    high: Math.round(adjusted * 1.05),
    effectiveLength,
    breedCorr, condCorr,
  };
};

// Result helpers
const water = (kg, condition) => ({
  rest: Math.round(kg * 0.05),
  training: Math.round(kg * 0.08),
  hot: Math.round(kg * 0.10),
});

const hay = (kg) => ({
  min: +(kg * 0.015).toFixed(1),
  optimal: +(kg * 0.020).toFixed(1),
  sport: +(kg * 0.025).toFixed(1),
});

// ---- UI helpers -----------------------------------------------------------

const KpiCard = ({ icon: Icon, label, value, sub, accent = false, testId }) => (
  <div className={`p-5 rounded-xl border ${accent ? "bg-amber-50 border-amber-300" : "bg-white border-[#E2E8F0]"}`} data-testid={testId}>
    {Icon && <Icon size={18} className={accent ? "text-amber-600" : "text-[#2563EB]"} />}
    <div className="text-[10px] uppercase tracking-widest text-[#64748B] mt-2">{label}</div>
    <div className="text-2xl font-bold text-[#0F172A] mt-1">{value}</div>
    {sub && <div className="text-[11px] text-[#94A3B8] mt-1">{sub}</div>}
  </div>
);

const ConditionMeter = ({ score }) => {
  const pct = Math.max(0, Math.min(100, ((score - 1) / 8) * 100));
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
      <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
        <span>1 — Very thin</span><span>5 — Ideal</span><span>9 — Obese</span>
      </div>
      <div className="relative h-3 rounded-full bg-gradient-to-r from-red-300 via-emerald-400 to-red-400 overflow-visible">
        <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#0F172A] border-2 border-white shadow-md" style={{ left: `calc(${pct}% - 8px)` }} />
      </div>
      <div className="text-center mt-3 text-sm text-[#0F172A] font-semibold">BCS {score}</div>
    </div>
  );
};

// ---- Main page -----------------------------------------------------------

const HorseWeightCalculatorPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [form, setForm] = useState({
    height: 165, heart: 195, length: 0,
    breed: "warmblood", condition: "ideal", age: "adult",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = isNl
      ? "Paardengewicht Calculator — schat het gewicht van je paard | HorseAppCompare"
      : "Horse Weight Calculator — estimate your horse's weight | HorseAppCompare";
    // Lightweight meta description for SEO
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", isNl
      ? "Bereken het gewicht van je paard, pony of ezel met borstomvang + stokmaat. Inclusief ras- en conditiecorrectie, dagelijkse ruwvoer- en waterbehoefte."
      : "Calculate the weight of your horse, pony or donkey from heart girth + height. Includes breed and body-condition correction, daily hay and water needs.");
  }, [isNl]);

  const result = useMemo(() => computeWeight(form), [form]);
  const W = result.estimated;
  const waterNeeds = useMemo(() => water(W, form.condition), [W, form.condition]);
  const hayNeeds = useMemo(() => hay(W), [W]);
  const cond = CONDITIONS.find((c) => c.id === form.condition);
  const breed = BREEDS.find((b) => b.id === form.breed);

  const analysis = useMemo(() => {
    const breedLabel = isNl ? breed.nl : breed.en;
    const condLabel = isNl ? cond.nl : cond.en;
    if (cond.id === "ideal") {
      return isNl
        ? `Op basis van stokmaat ${form.height} cm, borstomvang ${form.heart} cm en het ras ${breedLabel} wordt het gewicht geschat op ${W} kg. De conditie '${condLabel}' valt in de gezonde range — geen signalen van onder- of overgewicht. Meet de borstomvang maandelijks om trends tijdig te signaleren.`
        : `Based on a height of ${form.height} cm, heart girth ${form.heart} cm and the breed ${breedLabel}, the estimated weight is ${W} kg. The condition '${condLabel}' is within the healthy range — no signs of under- or overweight. Re-measure the heart girth monthly to spot trends early.`;
    }
    if (cond.correction < 0) {
      return isNl
        ? `Het paard wordt ingeschat op ${W} kg met conditie '${condLabel}'. Dit ligt onder de ideale BCS-range. Overweeg het rantsoen geleidelijk te verhogen (start met +10% ruwvoer) en laat een dierenarts of voedingsadviseur meekijken bij aanhoudend gewichtsverlies.`
        : `Estimated weight ${W} kg with condition '${condLabel}'. This is below the ideal BCS range. Consider gradually increasing the ration (start +10% hay) and consult a vet or nutritionist if weight loss persists.`;
    }
    return isNl
      ? `Het paard wordt ingeschat op ${W} kg met conditie '${condLabel}'. Dit ligt boven de ideale BCS-range. Verlaag krachtvoer, verhoog beweging en blijf ruwvoer op minimum 1,5% lichaamsgewicht aanbieden om vetstapeling veilig af te bouwen.`
      : `Estimated weight ${W} kg with condition '${condLabel}'. This is above the ideal BCS range. Reduce concentrates, increase exercise and keep hay at minimum 1.5% body weight to safely shed fat deposits.`;
  }, [W, form, breed, cond, isNl]);

  return (
    <main data-testid="weight-calc-page">
      <section className="bg-gradient-to-br from-[#EFF6FF] via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-14 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{isNl ? "Gewichtscalculator" : "Weight Calculator"}</span>
          </nav>
          <div className="overline mb-2 text-[#2563EB] inline-flex items-center gap-1.5"><Scale size={12} /> {isNl ? "Gratis tool" : "Free tool"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-3">
            {isNl ? "Paardengewicht Calculator" : "Horse Weight Calculator"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">
            {isNl
              ? "Schat het lichaamsgewicht van je paard, pony of ezel met de bewezen Carroll-Huntington formule, gecorrigeerd voor ras en lichaamsconditie. Inclusief richtlijn voor ruwvoer, water en medicatie."
              : "Estimate the body weight of your horse, pony or donkey using the proven Carroll-Huntington formula, corrected for breed and body condition. Includes daily hay, water and medication guidance."}
          </p>
        </div>
      </section>

      <section className="hac-container py-10 max-w-6xl grid lg:grid-cols-2 gap-6">
        {/* Input form */}
        <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5">
          <h2 className="font-bold text-xl text-[#0F172A] mb-1">{isNl ? "Voer je metingen in" : "Enter your measurements"}</h2>
          <p className="text-xs text-[#94A3B8]">{isNl ? "Resultaat wordt direct berekend terwijl je typt." : "Result is calculated live as you type."}</p>

          <div>
            <label className="overline block mb-1 text-[#64748B]">{isNl ? "Stokmaat" : "Height at withers"} (cm) *</label>
            <input type="number" min="40" max="220" required className="hac-input" value={form.height} onChange={(e) => setForm({ ...form, height: parseInt(e.target.value || "0") })} data-testid="calc-height" />
            <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "Van de grond tot de schoft." : "From the ground to the withers."}</p>
          </div>

          <div>
            <label className="overline block mb-1 text-[#64748B]">{isNl ? "Borstomvang" : "Heart girth"} (cm) *</label>
            <input type="number" min="40" max="280" required className="hac-input" value={form.heart} onChange={(e) => setForm({ ...form, heart: parseInt(e.target.value || "0") })} data-testid="calc-heart" />
            <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "Direct achter de schoft rondom de borstkas." : "Directly behind the withers, around the chest."}</p>
          </div>

          <div>
            <label className="overline block mb-1 text-[#64748B]">{isNl ? "Lichaamslengte" : "Body length"} (cm) <span className="text-[#94A3B8] font-normal normal-case">— {isNl ? "optioneel" : "optional"}</span></label>
            <input type="number" min="0" max="280" className="hac-input" value={form.length || ""} onChange={(e) => setForm({ ...form, length: parseInt(e.target.value || "0") })} data-testid="calc-length" />
            <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "Van schouderpunt tot zitbeenknobbel. Indien leeg: geschat uit stokmaat." : "From point of shoulder to point of buttock. If empty: estimated from height."}</p>
          </div>

          <div>
            <label className="overline block mb-1 text-[#64748B]">{isNl ? "Ras / type" : "Breed / type"}</label>
            <select className="hac-input" value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="calc-breed">
              {BREEDS.map((b) => <option key={b.id} value={b.id}>{isNl ? b.nl : b.en} {b.correction !== 0 && `(${b.correction > 0 ? "+" : ""}${Math.round(b.correction * 100)}%)`}</option>)}
            </select>
          </div>

          <div>
            <label className="overline block mb-1 text-[#64748B]">{isNl ? "Conditiescore (BCS)" : "Body Condition Score"}</label>
            <select className="hac-input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="calc-condition">
              {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{isNl ? c.nl : c.en}</option>)}
            </select>
          </div>

          <div>
            <label className="overline block mb-1 text-[#64748B]">{isNl ? "Leeftijd" : "Age"}</label>
            <select className="hac-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="calc-age">
              {AGES.map((a) => <option key={a.id} value={a.id}>{isNl ? a.nl : a.en}</option>)}
            </select>
          </div>
        </form>

        {/* Result panel */}
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#0F172A] to-[#1e293b] text-white rounded-2xl p-6" data-testid="calc-result">
            <div className="text-xs uppercase tracking-widest text-amber-300 mb-2 inline-flex items-center gap-1.5">
              <Sparkles size={12} /> {isNl ? "Geschat gewicht" : "Estimated weight"}
            </div>
            <div className="text-5xl md:text-6xl font-bold tabular-nums" data-testid="calc-weight">{W} <span className="text-2xl text-[#94A3B8]">kg</span></div>
            <div className="text-sm text-[#94A3B8] mt-2">
              {isNl ? "Waarschijnlijk tussen" : "Likely between"} <strong className="text-white">{result.low}</strong> — <strong className="text-white">{result.high} kg</strong>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-3 text-[11px]">
              <div><div className="text-[#94A3B8] uppercase">{isNl ? "Basis" : "Base"}</div><div className="font-mono">{result.base} kg</div></div>
              <div><div className="text-[#94A3B8] uppercase">{isNl ? "Ras" : "Breed"}</div><div className={`font-mono ${result.breedCorr < 0 ? "text-red-300" : result.breedCorr > 0 ? "text-emerald-300" : "text-[#94A3B8]"}`}>{result.breedCorr > 0 ? "+" : ""}{Math.round(result.breedCorr * 100)}%</div></div>
              <div><div className="text-[#94A3B8] uppercase">{isNl ? "BCS" : "BCS"}</div><div className={`font-mono ${result.condCorr < 0 ? "text-red-300" : result.condCorr > 0 ? "text-amber-300" : "text-[#94A3B8]"}`}>{result.condCorr > 0 ? "+" : ""}{Math.round(result.condCorr * 100)}%</div></div>
            </div>
          </div>

          <ConditionMeter score={cond.score} />

          <div className="grid grid-cols-3 gap-3">
            <KpiCard icon={Droplet} label={isNl ? "Water (rust)" : "Water (rest)"} value={`${waterNeeds.rest} L`} sub={isNl ? "per dag" : "per day"} testId="calc-water-rest" />
            <KpiCard icon={Droplet} label={isNl ? "Water (training)" : "Water (training)"} value={`${waterNeeds.training} L`} sub={isNl ? "per dag" : "per day"} testId="calc-water-training" />
            <KpiCard icon={Droplet} label={isNl ? "Water (warm)" : "Water (hot day)"} value={`${waterNeeds.hot} L`} sub={isNl ? "per dag" : "per day"} testId="calc-water-hot" />
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <h3 className="font-bold text-[#0F172A] mb-3 inline-flex items-center gap-2"><Wheat size={16} className="text-amber-600" /> {isNl ? "Richtlijn ruwvoer (hooi)" : "Hay guideline"}</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-[10px] uppercase text-[#64748B]">{isNl ? "Minimum" : "Minimum"}</div><div className="text-xl font-bold text-[#0F172A]">{hayNeeds.min}</div><div className="text-[10px] text-[#94A3B8]">kg · 1.5%</div></div>
              <div className="bg-amber-50 rounded-lg py-2 -my-2"><div className="text-[10px] uppercase text-amber-700">{isNl ? "Optimaal" : "Optimal"}</div><div className="text-xl font-bold text-amber-900">{hayNeeds.optimal}</div><div className="text-[10px] text-amber-700">kg · 2.0%</div></div>
              <div><div className="text-[10px] uppercase text-[#64748B]">{isNl ? "Sport" : "Sport"}</div><div className="text-xl font-bold text-[#0F172A]">{hayNeeds.sport}</div><div className="text-[10px] text-[#94A3B8]">kg · 2.5%</div></div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="calc-analysis">
            <h3 className="font-bold text-[#0F172A] mb-2 inline-flex items-center gap-2"><Sparkles size={16} className="text-amber-600" /> {isNl ? "Analyse" : "Analysis"}</h3>
            <p className="text-sm text-[#0F172A] leading-relaxed">{analysis}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm flex gap-2 items-start" data-testid="calc-disclaimer">
            <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-900 mb-0.5"><Pill size={12} className="inline mr-1" /> {isNl ? "Medicatie-disclaimer" : "Medication disclaimer"}</div>
              <p className="text-red-800 text-xs">{isNl ? WEIGHT_DISCLAIMER_NL : WEIGHT_DISCLAIMER_EN}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO copy */}
      <section className="bg-[#F8FAFC] border-t border-[#E2E8F0]">
        <div className="hac-container py-12 max-w-3xl prose prose-sm">
          <h2 className="font-heading text-2xl text-[#0F172A] mb-3">{isNl ? "Hoe meet je je paard correct?" : "How do you measure your horse correctly?"}</h2>
          <p className="text-[#64748B] leading-relaxed">{isNl
            ? "Gebruik een flexibele meetlint en meet de borstomvang direct achter de schoft, rond de breedste plek van de borstkas. Houd het lint strak maar niet beknellend. Meet 's morgens vóór het eten voor de meest consistente waarde. Voor de stokmaat: laat het paard recht op vier benen staan op een vlakke ondergrond, meet vanaf de grond tot het hoogste punt van de schoft."
            : "Use a flexible measuring tape and measure the heart girth directly behind the withers, around the widest part of the chest. Keep the tape snug but not tight. Measure in the morning before feeding for the most consistent value. For the height: stand the horse square on four legs on a flat surface, measure from the ground to the highest point of the withers."}</p>
          <h2 className="font-heading text-2xl text-[#0F172A] mt-8 mb-3">{isNl ? "Waarom is gewicht belangrijk?" : "Why does weight matter?"}</h2>
          <p className="text-[#64748B] leading-relaxed">{isNl
            ? "Een nauwkeurige gewichtsschatting is de basis voor voeding (ruwvoer percentage), medicatiedosering (wormkuren, NSAIDs, antibiotica), en sportprestatie. Een afwijking van 50 kg kan al een significante over- of onderdosering veroorzaken. Combineer deze calculator daarom met een Body Condition Score (BCS) en, voor exacte dosering, een weegbrug."
            : "An accurate weight estimate is the foundation for feeding (hay percentage), medication dosing (dewormers, NSAIDs, antibiotics) and athletic performance. A 50 kg deviation can already cause significant over- or underdosing. Combine this calculator with a Body Condition Score (BCS) and, for exact dosing, a livestock scale."}</p>
          <div className="mt-8 p-5 bg-white border border-[#E2E8F0] rounded-xl text-center">
            <Info size={20} className="text-[#2563EB] mx-auto mb-2" />
            <p className="text-sm text-[#64748B] mb-3">{isNl ? "Ben je op zoek naar een app om je paard te volgen?" : "Looking for an app to track your horse?"}</p>
            <Link to="/apps" className="hac-btn-primary inline-flex">{isNl ? "Vergelijk paarden-apps" : "Compare horse apps"} <ChevronRight size={14} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HorseWeightCalculatorPage;
